import type { RecordRow } from '../../data/client';
import { todayKey } from '../record/dueDate';

/**
 * READING A ROW A PANEL ALREADY FETCHED.
 *
 * Small on purpose: everything here works on values the panel's ONE `RecordQuery` put in
 * the cache, and nothing in it can issue a request. That is the row budget expressed as a
 * module boundary - if a row field needed a second query, it would have to be a decision
 * somewhere else, and the document already decided against it.
 */

export function textOf(row: RecordRow, fieldId: string | undefined): string | null {
  if (fieldId === undefined) return null;
  const value = row.values[fieldId];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The label a RELATION already carries (`relationLabels`), never a second read.
 *
 * `POST /api/query` resolves every relation's target label in ONE statement per relation
 * FIELD, and the browser keeps it BESIDE the value rather than inside it - a `{id,label}`
 * where a uuid is expected would make every relation condition in the shared JS evaluator
 * false. So a row renders an agency's name with no request of its own.
 */
export function relationLabelOf(row: RecordRow, fieldId: string | undefined): string | null {
  if (fieldId === undefined) return null;
  const label = row.relationLabels?.[fieldId];
  return typeof label === 'string' && label.length > 0 ? label : null;
}

/**
 * "set 12 days ago" from a DATE, in CALENDAR DAYS.
 *
 * Never in instants: `dueDate.ts` settled that for tasks, and both of this panel's dates
 * are DATE for the same reason - two adjacent ages computed from two different temporal
 * families is how the two lanes drift by a day against each other.
 */
export function ageInDays(day: string | null, verb = 'set'): string | null {
  if (day === null) return null;
  const [year, month, date] = day.split('-').map(Number);
  if (year === undefined || month === undefined || date === undefined) return null;
  const then = new Date(year, month - 1, date);
  const [nowYear, nowMonth, nowDate] = todayKey().split('-').map(Number);
  if (nowYear === undefined || nowMonth === undefined || nowDate === undefined) return null;
  const now = new Date(nowYear, nowMonth - 1, nowDate);
  const days = Math.round((now.getTime() - then.getTime()) / 86_400_000);
  if (days < 0) return `${verb} in ${String(-days)} days`;
  if (days === 0) return `${verb} today`;
  if (days === 1) return `${verb} yesterday`;
  return `${verb} ${String(days)} days ago`;
}

/**
 * THE FIRST LINE AND ONLY THE FIRST. `health_reason` is a multiline TEXT holding one
 * sentence per finding, worst first, and a row is one line high - so a row shows the worst
 * one and the record page shows all of them. It lives here rather than in one panel
 * because both panels that draw a health row need the same first line (wave 3, H-C3).
 */
export function firstReasonOf(reason: string | null): string | null {
  if (reason === null) return null;
  const first = reason.split('\n')[0]?.trim();
  return first === undefined || first === '' ? null : first;
}

/** `2026-08-03T09:30:00+02:00` as `09:30`, in the reader's own zone. */
export function timeOf(value: string | null): string | null {
  if (value === null) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
}
